import { useState } from "react";

import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
} from "@mui/material";

import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

function ActiveAlarm({ alarm, onAcknowledge, onSilence }) {
  const [openDetails, setOpenDetails] = useState(false);

  if (!alarm) {
    return (
      <Box
      sx={{
        border: "1px solid #ef4444",
        borderRadius: "14px",
        px: { xs: 1.5, sm: 2, md: 2.5 },
        py: { xs: 1.5, md: 2 },
        position: "relative",
        overflow: "hidden",
        background: "#ffe5e5",
        animation: "alarmBackgroundBlink 1.2s infinite ease-in-out",
        transition: "all 0.3s ease",
        width: "100%",          
        alignSelf: "flex-start", 
        boxSizing: "border-box",
      }}
      >
        <Typography
          sx={{
            color: "#6b7280",
            fontWeight: 500,
            fontSize: { xs: "0.82rem", md: "0.92rem" },  
          }}
        >
          No active alarm
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes alarmBackgroundBlink {
            0%   { background: #fff7f7; }
            50%  { background: #ffe5e5; }
            100% { background: #fff7f7; }
          }
        `}
      </style>

      <Box
        sx={{
          border: "1px solid #ef4444",
          borderRadius: "14px",
          px: { xs: 1.5, sm: 2, md: 2.5 },               
          py: { xs: 1.5, md: 2 },
          position: "relative",
          overflow: "hidden",
          background: "#ffe5e5",
          animation: "alarmBackgroundBlink 1.2s infinite ease-in-out",
          transition: "all 0.3s ease",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: { xs: 1.5, md: 3 },                      
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          {/* ── LEFT ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1.5, md: 2 },                    
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* ICON */}
            <Box
              sx={{
                width: { xs: 38, sm: 42, md: 48 },        
                height: { xs: 38, sm: 42, md: 48 },
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <WarningAmberRoundedIcon
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: 18, sm: 20, md: 24 },  
                }}
              />
            </Box>

            {/* TEXT */}
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, md: 2 },
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: { xs: "0.68rem", sm: "0.72rem", md: "0.78rem" }, 
                    fontWeight: 700,
                    fontFamily: "'IBM Plex Mono', monospace",
                  }}
                >
                  DEVICE ID : {alarm.deviceId}
                </Typography>

                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: { xs: "0.68rem", sm: "0.72rem", md: "0.78rem" },
                    fontWeight: 700,
                    fontFamily: "'IBM Plex Mono', monospace",
                  }}
                >
                  TIMESTAMP : {alarm.timestamp || "NA"}
                </Typography>
              </Box>

              {/* <Typography
                sx={{
                  color: "#4b5563",
                  fontSize: { xs: "0.72rem", sm: "0.76rem", md: "0.82rem" },  
                  fontWeight: 700,
                  fontFamily: "'IBM Plex Mono', monospace",
                  letterSpacing: "0.02em",
                  wordBreak: "break-all",
                  mt: 0.3,
                }}
              >
                Payload : {alarm.payload}
              </Typography> */}
            </Box>
          </Box>

          {/* ── BUTTONS ── */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, md: 1.4 },
              flexShrink: 0,
              flexWrap: { xs: "wrap", sm: "nowrap" },     
              width: { xs: "100%", sm: "auto" },           
            }}
          >
            <Button
              startIcon={<CheckIcon />}
              onClick={onAcknowledge}
              variant="contained"
              disableElevation
              sx={{
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                color: "#fff",
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 700,
                px: { xs: 1.5, md: 2.5 },                 
                minHeight: { xs: 36, md: 42 },             
                fontSize: { xs: "0.8rem", md: "0.875rem" },
                flex: { xs: 1, sm: "none" },               
                "&:hover": {
                  background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                },
              }}
            >
              Acknowledge
            </Button>

            <Button
              startIcon={<OpenInNewIcon />}
              variant="contained"
              disableElevation
              onClick={() => setOpenDetails(true)}
              sx={{
                background: "#e5e7eb",
                color: "#4b5563",
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 700,
                px: { xs: 1.5, md: 2.5 },                
                minHeight: { xs: 36, md: 42 },            
                fontSize: { xs: "0.8rem", md: "0.875rem" },
                boxShadow: "none",
                flex: { xs: 1, sm: "none" },              
                "&:hover": {
                  background: "#d1d5db",
                  boxShadow: "none",
                },
              }}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── POPUP ── */}
      <Dialog
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            pt: 2,
            borderRadius: "16px",
            mx: { xs: 2, sm: "auto" },                    
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "#1f2937",
            px: 3,
            pt: 2,
            pb: 2,
            fontSize: { xs: "1rem", md: "1.25rem" },      
          }}
        >
          Alarm Details
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 3, px: { xs: 2, md: 3 } }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>
                Event ID
              </Typography>
              <Typography sx={{ color: "#111827", fontSize: { xs: "0.85rem", md: "1rem" } }}>
                EVT-2026-001
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>
                Status
              </Typography>
              <Typography
                sx={{
                  color: "#ef4444",
                  fontWeight: 700,
                  fontSize: { xs: "0.85rem", md: "1rem" },
                }}
              >
                ACTIVE
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>
                Device ID
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.85rem", md: "1rem" } }}>
                {alarm.deviceId || "NA"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>
                Name
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.85rem", md: "1rem" } }}>
                ******
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>
                Location
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.85rem", md: "1rem" } }}>
                ******
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>
                Timestamp
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.85rem", md: "1rem" } }}>
                {alarm?.timestamp || "NA"}
              </Typography>
            </Grid>

            {/* <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>
                Payload
              </Typography>
              <Typography
                sx={{
                  wordBreak: "break-all",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: { xs: "0.75rem", md: "0.875rem" },  // ✅ responsive
                }}
              >
                {alarm.payload || "NA"}
              </Typography>
            </Grid> */}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ActiveAlarm;